import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'motion/react'
import { ChevronDown, Gem, Users } from 'lucide-react'

// Множители выигрыша по цветам
const COLOR_MULTIPLIER = { red: 2, black: 2, green: 14 }
// Длительность анимации счётчика (мс)
const COUNTER_DURATION = 1000
const SKIN_SWAP_PRELOAD_TIMEOUT = 1200

// Анимированный счётчик суммы: плавно меняет число от startValue к targetValue за COUNTER_DURATION мс
const AnimatedSum = ({ value, isWinner, isLoser, isAnimating }) => {
  const [displayValue, setDisplayValue] = useState(value)
  const rafRef = useRef(null)
  const startTsRef = useRef(null)
  const startValRef = useRef(value)

  useEffect(() => {
    if (!isAnimating) {
      // Сброс — показываем реальное значение
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      setDisplayValue(value)
      return
    }

    const from = value
    const to = isWinner ? value : 0
    startValRef.current = from
    startTsRef.current = null

    const tick = (ts) => {
      if (!startTsRef.current) startTsRef.current = ts
      const elapsed = ts - startTsRef.current
      const progress = Math.min(elapsed / COUNTER_DURATION, 1)
      // easeOutQuad для плавного замедления
      const eased = 1 - (1 - progress) * (1 - progress)
      const current = Math.round(from + (to - from) * eased)
      setDisplayValue(current)

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick)
      }
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
  }, [isAnimating, isWinner, isLoser, value])

  // Для победителей после анимации уменьшения — показываем оригинал, умножение отображается отдельно
  return <>{Math.max(0, displayValue).toLocaleString()}</>
}

// Анимированный множитель суммы для победителей: value → value * multiplier
const AnimatedWinSum = ({ value, multiplier, isAnimating }) => {
  const target = value * multiplier
  const [displayValue, setDisplayValue] = useState(value)
  const rafRef = useRef(null)
  const startTsRef = useRef(null)

  useEffect(() => {
    if (!isAnimating) {
      setDisplayValue(value)
      return
    }

    startTsRef.current = null

    const tick = (ts) => {
      if (!startTsRef.current) startTsRef.current = ts
      const elapsed = ts - startTsRef.current
      const progress = Math.min(elapsed / COUNTER_DURATION, 1)
      const eased = 1 - (1 - progress) * (1 - progress)
      const current = Math.round(value + (target - value) * eased)
      setDisplayValue(current)

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick)
      }
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
  }, [isAnimating, value, target])

  return <>{displayValue.toLocaleString()}</>
}

// Анимированный счётчик для плитки кристаллов: value → value * multiplier
const AnimatedCrystalSum = ({ value, multiplier, isAnimating }) => {
  const target = value * multiplier
  const [displayValue, setDisplayValue] = useState(value)
  const rafRef = useRef(null)
  const startTsRef = useRef(null)

  useEffect(() => {
    if (!isAnimating) {
      setDisplayValue(value)
      return
    }

    startTsRef.current = null

    const tick = (ts) => {
      if (!startTsRef.current) startTsRef.current = ts
      const elapsed = ts - startTsRef.current
      const progress = Math.min(elapsed / COUNTER_DURATION, 1)
      const eased = 1 - (1 - progress) * (1 - progress)
      const current = Math.round(value + (target - value) * eased)
      setDisplayValue(current)

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick)
      }
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
  }, [isAnimating, value, target])

  return <>{displayValue.toLocaleString()}</>
}

const getBetKey = (bet, columnKey, index) => {
  const stableId = bet?.id ?? bet?.bet_id ?? bet?.created_at ?? null

  if (stableId !== null && stableId !== undefined && stableId !== '') {
    return `${columnKey}-${stableId}`
  }

  if (bet?.chat_id) {
    return `${columnKey}-${bet.chat_id}-${Number(bet?.sum || 0)}-${Number(bet?.crystal || 0)}`
  }

  return `${columnKey}-fallback-${index}-${String(bet?.name || 'player')}`
}

const AnimatedBetSkins = ({
  baseSkins,
  winSkins,
  isWinner,
  multiplier,
  crystalAmount,
  itemKeyPrefix
}) => {
  const [showWinSkins, setShowWinSkins] = useState(false)

  useEffect(() => {
    if (!isWinner || winSkins.length === 0) {
      setShowWinSkins(false)
      return
    }

    let isCancelled = false
    let timeoutId = null

    const imageUrls = winSkins
      .map((item) => item?.img)
      .filter(Boolean)

    const preloadPromise = Promise.all(
      imageUrls.map((src) => new Promise((resolve) => {
        const image = new window.Image()
        image.onload = resolve
        image.onerror = resolve
        image.src = src
      }))
    )

    const timeoutPromise = new Promise((resolve) => {
      timeoutId = setTimeout(resolve, SKIN_SWAP_PRELOAD_TIMEOUT)
    })

    Promise.race([preloadPromise, timeoutPromise]).then(() => {
      if (isCancelled) return
      setShowWinSkins(true)
    })

    return () => {
      isCancelled = true
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [isWinner, winSkins])

  const renderedSkins = showWinSkins && isWinner && winSkins.length > 0 ? winSkins : baseSkins
  const hasDisplayedSkins = renderedSkins.length > 0
  const isWinView = showWinSkins && isWinner && winSkins.length > 0
  const safeCrystalAmount = Number(crystalAmount || 0)

  if (!hasDisplayedSkins) {
    return null
  }

  return (
    <div className="mt-3 relative">
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={isWinView ? 'win-skins' : 'base-skins'}
          initial={{ opacity: 0, scale: 0.96, filter: 'blur(2px)' }}
          animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
          exit={{ opacity: 0, scale: 1.03, filter: 'blur(3px)' }}
          transition={{ duration: 0.46, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-3 gap-2"
        >
          {renderedSkins.map((item, itemIndex) => (
            <motion.div
              key={`${item.id || itemIndex}-${itemKeyPrefix}`}
              initial={{ opacity: 0, y: 10, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.32, delay: Math.min(itemIndex, 3) * 0.05 }}
              className={`relative overflow-hidden rounded-md border p-1.5 flex flex-col items-center justify-center text-center ${isWinView ? 'border-accent-primary/45 bg-accent-primary/12 shadow-[0_0_16px_rgba(0,255,127,0.28)]' : 'border-white/10 bg-dark-primary/60'}`}
            >
              <div className="relative w-full h-12">
                <img
                  src={item.img}
                  alt={item.name}
                  className="w-full h-12 object-contain mx-auto"
                  loading="lazy"
                />
                {isWinView ? (
                  <motion.div
                    className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/35 to-transparent"
                    animate={{ x: ['-120%', '140%'] }}
                    transition={{ duration: 0.7, ease: 'easeOut', delay: Math.min(itemIndex, 3) * 0.08 }}
                  />
                ) : null}
              </div>
              <p className={`mt-1 text-[10px] inline-flex items-center justify-center gap-0.5 w-full ${isWinView ? 'text-accent-primary font-medium' : 'text-accent-primary'}`}>
                {Number(item.cost ?? item.price ?? 0).toLocaleString()} <Gem className="w-3 h-3" />
              </p>
            </motion.div>
          ))}

          {hasDisplayedSkins && safeCrystalAmount > 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.32, delay: Math.min(renderedSkins.length, 3) * 0.05 }}
              className="rounded-md border border-cyan-300/30 bg-cyan-500/10 p-1.5 flex flex-col items-center justify-center text-center"
            >
              <div className="w-full h-12 flex items-center justify-center">
                <Gem className="w-6 h-6 text-cyan-200" />
              </div>
              <p className="mt-1 text-[10px] text-cyan-100 inline-flex items-center justify-center gap-0.5 w-full">
                {isWinView ? (
                  <AnimatedCrystalSum value={safeCrystalAmount} multiplier={multiplier} isAnimating={isWinView} />
                ) : (
                  safeCrystalAmount.toLocaleString()
                )} <Gem className="w-3 h-3" />
              </p>
            </motion.div>
          ) : null}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

const DoubleBetsFeed = ({ bets = [], roundStatus, winColor }) => {
  // Затемнение колонок: при кручении все затемнены, при результате — только проигравшие
  const isSpinning = roundStatus === 'raffle'
  const isResult = ['result', 'finished'].includes(roundStatus)

  const getColumnDimmed = (colorKey) => {
    if (isSpinning) return true
    if (isResult && winColor && winColor !== colorKey) return true
    return false
  }
  const { t } = useTranslation()

  const colorColumns = [
    {
      key: 'red',
      cardClass: 'border-red-400/35 bg-red-500/12'
    },
    {
      key: 'black',
      cardClass: 'border-white/20 bg-slate-900/45'
    },
    {
      key: 'green',
      cardClass: 'border-emerald-400/35 bg-emerald-500/12'
    }
  ]

  const safeBets = Array.isArray(bets) ? bets : []
  const [expandedMobileColor, setExpandedMobileColor] = useState(null)

  const columnData = colorColumns.map((column) => {
    const columnBets = safeBets
      .filter((bet) => String(bet.color || '') === column.key)
      .sort((a, b) => Number(b?.sum || 0) - Number(a?.sum || 0))

    const playersCount = new Set(
      columnBets.map((bet, index) => String(bet.chat_id || `${bet.name || 'player'}-${index}`))
    ).size

    const totalSum = columnBets.reduce((acc, bet) => acc + Number(bet?.sum || 0), 0)

    return {
      ...column,
      bets: columnBets,
      playersCount,
      totalSum
    }
  })

  const renderBetCard = (bet, index, column, isColumnWinner, isColumnLoser, multiplier) => {
    const betItems = Array.isArray(bet.bet_items) ? bet.bet_items : []
    const betSum = Number(bet.sum || 0)
    const isMyBet = Boolean(bet.your_bet)
    const winSkinsRaw = Array.isArray(bet.win_skin)
      ? bet.win_skin
      : (bet.win_skin ? [bet.win_skin] : [])

    return (
      <motion.div
        key={getBetKey(bet, column.key, index)}
        layout
        initial={{ opacity: 0, y: -26, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 22, scale: 0.96 }}
        transition={{
          duration: 0.38,
          ease: [0.22, 1, 0.36, 1],
          delay: Math.min(index, 6) * 0.035
        }}
        className={`rounded-xl border p-3 ${column.cardClass} ${isMyBet ? '!bg-accent-primary/15 !border-accent-primary/45 ring-1 ring-accent-primary/35' : ''}`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <img
              src={bet.ava}
              alt={bet.name}
              className="w-9 h-9 rounded-full object-cover border border-white/20"
            />
            <div className="min-w-0">
              <p className="text-sm text-white font-semibold truncate">{bet.name || t('double.common.player', 'Игрок')}</p>
            </div>
          </div>

          <span className={`inline-flex items-center gap-1 text-sm font-semibold ${isColumnWinner ? 'text-emerald-400' : isColumnLoser ? 'text-red-400' : 'text-accent-primary'}`}>
            {isColumnWinner ? (
              <AnimatedWinSum value={betSum} multiplier={multiplier} isAnimating={isResult} />
            ) : isColumnLoser ? (
              <AnimatedSum value={betSum} isWinner={false} isLoser={true} isAnimating={isResult} />
            ) : (
              betSum.toLocaleString()
            )}
            {' '}<Gem className="w-4 h-4" />
          </span>
        </div>

        <AnimatedBetSkins
          baseSkins={betItems}
          winSkins={winSkinsRaw}
          isWinner={isColumnWinner}
          multiplier={multiplier}
          crystalAmount={Number(bet.crystal || 0)}
          itemKeyPrefix={`${column.key}-${bet.chat_id || 'anon'}-${bet.id || index}`}
        />
      </motion.div>
    )
  }

  return (
    <div>
      <div className="space-y-2 lg:hidden">
        <p className="text-xs font-semibold uppercase tracking-wide text-white/65 px-1">
          {t('double.bets.currentRound', 'Ставки текущего раунда')}
        </p>

        {columnData.map((column) => {
          const isOpen = expandedMobileColor === column.key
          const isColumnWinner = isResult && winColor === column.key
          const isColumnLoser = isResult && winColor && winColor !== column.key
          const multiplier = COLOR_MULTIPLIER[column.key] || 2

          const colorDotClass = column.key === 'red'
            ? 'bg-red-500 border-red-300/70'
            : column.key === 'black'
              ? 'bg-slate-900 border-white/45'
              : 'bg-emerald-500 border-emerald-300/70'

          return (
            <div key={column.key} className={`rounded-xl border border-white/10 bg-dark-secondary/80 transition-opacity duration-500 ${getColumnDimmed(column.key) ? 'opacity-30' : 'opacity-100'}`}>
              <button
                type="button"
                onClick={() => setExpandedMobileColor((prev) => (prev === column.key ? null : column.key))}
                className="w-full px-3 py-2.5 flex items-center justify-between gap-2"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`inline-flex w-4 h-4 rounded-full border-2 ${colorDotClass}`} />
                  <span className="inline-flex items-center gap-1 text-xs text-white/70">
                    <Users className="w-3.5 h-3.5" />
                    {column.playersCount}
                  </span>
                </div>

                <div className="flex items-center gap-2 text-accent-primary">
                  <span className="inline-flex items-center gap-1 text-sm font-semibold">
                    {column.totalSum.toLocaleString()} <Gem className="w-4 h-4" />
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                </div>
              </button>

              <AnimatePresence initial={false}>
                {isOpen ? (
                  <motion.div
                    key={`${column.key}-mobile-content`}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="px-2 pb-2 space-y-2 max-h-[360px] overflow-y-auto">
                      <AnimatePresence initial={false}>
                        {column.bets.map((bet, index) => renderBetCard(bet, index, column, isColumnWinner, isColumnLoser, multiplier))}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          )
        })}
      </div>

      <div className="hidden lg:grid gap-3 lg:grid-cols-3">
        {columnData.map((column) => {
          const isColumnWinner = isResult && winColor === column.key
          const isColumnLoser = isResult && winColor && winColor !== column.key
          const multiplier = COLOR_MULTIPLIER[column.key] || 2

          return (
            <div key={column.key} className={`min-h-0 transition-opacity duration-500 ${getColumnDimmed(column.key) ? 'opacity-30' : 'opacity-100'}`}>
              <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
                <AnimatePresence initial={false}>
                  {column.bets.map((bet, index) => renderBetCard(bet, index, column, isColumnWinner, isColumnLoser, multiplier))}
                </AnimatePresence>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default DoubleBetsFeed
